<template>
  <page-header-wrapper>
    <div class="operation-rule-page erp-list-page baidao-list-page">
      <div class="erp-search-card">
        <a-form layout="inline">
          <a-row :gutter="24">
            <a-col :md="6" :sm="24">
              <a-form-item label="运营规则模版名称">
                <a-input v-model="query.ruleName" allow-clear placeholder="请输入" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="8" :sm="24">
              <a-form-item label="区域">
                <region-cascader v-model="query.regionCode" />
              </a-form-item>
            </a-col>
            <a-col :md="24" :sm="24" class="erp-search-actions">
              <a-button type="primary" icon="search" @click="search">搜索</a-button>
              <a-button icon="redo" @click="reset">重置</a-button>
            </a-col>
          </a-row>
        </a-form>
      </div>

      <div class="erp-table-card">
        <div class="erp-table-toolbar">
          <div>
            <h3>运营规则</h3>
            <p>按区域维护营业时段。同一区域可以配置多条运营规则。</p>
          </div>
          <div class="erp-toolbar-actions">
            <a-button icon="sync" :loading="syncing" :disabled="!selectedRowKeys.length" @click="syncSelected">同步数据</a-button>
            <a-button type="primary" icon="plus" @click="openCreate">新增运营规则模版</a-button>
            <a-button icon="download" :loading="exporting" @click="exportRows">导出</a-button>
          </div>
        </div>
        <a-table
          :row-key="rowKey"
          :columns="columns"
          :data-source="rows"
          :loading="loading"
          :pagination="pagination"
          :row-selection="rowSelection"
          bordered
          size="middle"
          :scroll="{ x: 1100 }"
          @change="onPageChange">
          <template slot="remark" slot-scope="value">{{ value || '无' }}</template>
          <template slot="operation" slot-scope="value, record">
            <a-button size="small" @click="openView(record)">查看</a-button>
            <a-button size="small" @click="openEdit(record)">编辑</a-button>
            <a-button size="small" @click="remove(record)">删除</a-button>
          </template>
        </a-table>
      </div>

      <a-drawer
        :visible="drawerVisible"
        :width="drawerWidth"
        :closable="false"
        :body-style="{ padding: 0 }"
        :mask-closable="false"
        class="erp-doc-drawer baidao-drawer-sample"
        @close="closeDrawer">
        <div class="erp-drawer-shell">
          <header class="erp-drawer-header">
            <div>
              <button class="erp-back-button" type="button" title="返回列表" aria-label="返回列表" @click="closeDrawer">
                <a-icon type="arrow-left" />
              </button>
              <h3>{{ drawerTitle }}</h3>
            </div>
            <div>
              <a-button @click="closeDrawer">取消</a-button>
              <a-button v-if="!readOnly" type="primary" :loading="saving" @click="submit">确定</a-button>
            </div>
          </header>
          <div class="erp-drawer-content">
            <section class="erp-form-card">
              <div class="erp-section-title">
                <span class="erp-section-index">01</span>
                <div><h4>基本信息</h4></div>
              </div>
              <div class="erp-base-grid">
                <label>
                  <span><b class="erp-required">*</b> 运营规则名称</span>
                  <a-input v-model="form.ruleName" :disabled="readOnly" maxlength="128" placeholder="请输入" />
                </label>
                <label class="wide">
                  <span><b class="erp-required">*</b> 区域</span>
                  <region-cascader v-model="form.regionCode" :disabled="readOnly" :fallback-text="form.regionName" />
                </label>
                <label class="wide">
                  <span>备注</span>
                  <a-input v-model="form.remark" :disabled="readOnly" maxlength="500" placeholder="请输入" />
                </label>
              </div>
            </section>
            <section class="erp-form-card">
              <div class="erp-section-title">
                <span class="erp-section-index">02</span>
                <div><h4>下单时间配置</h4></div>
              </div>
              <div class="erp-base-grid">
                <label>
                  <span><b class="erp-required">*</b> 营业开始时间</span>
                  <a-time-picker
                    :value="timeValue(form.businessStartTime)"
                    :disabled="readOnly"
                    format="HH:mm"
                    :show-second="false"
                    placeholder="请选择时间"
                    style="width:100%"
                    @change="(time, text) => { form.businessStartTime = text || '' }" />
                </label>
                <label>
                  <span><b class="erp-required">*</b> 营业结束时间</span>
                  <a-time-picker
                    :value="timeValue(form.businessEndTime)"
                    :disabled="readOnly"
                    format="HH:mm"
                    :show-second="false"
                    placeholder="请选择时间"
                    style="width:100%"
                    @change="(time, text) => { form.businessEndTime = text || '' }" />
                </label>
              </div>
            </section>
          </div>
        </div>
      </a-drawer>
    </div>
  </page-header-wrapper>
</template>

<script>
import moment from 'moment'
import { addOperationRule, deleteOperationRule, editOperationRule, pageOperationRule, syncOperationRule } from '@/api/erp/operationRule'
import RegionCascader from './RegionCascader.vue'
import { loadChinaArea, regionLabel, regionSubtreeIds } from './regionChina'

function blankForm () {
  return { id: undefined, ruleName: '', regionCode: undefined, regionName: '', businessStartTime: '', businessEndTime: '', remark: '' }
}

function normalizeTimeText (value) {
  const text = String(value || '').trim()
  if (!text) return ''
  const parsed = moment(text, ['HH:mm', 'HH:mm:ss'], true)
  return parsed.isValid() ? parsed.format('HH:mm') : ''
}

function timeText (value) {
  return normalizeTimeText(value) || '—'
}

function timeValue (value) {
  const normalized = normalizeTimeText(value)
  if (!normalized) return null
  const parsed = moment(normalized, 'HH:mm', true)
  return parsed.isValid() ? parsed : null
}

export default {
  name: 'ErpOperationRule',
  components: { RegionCascader },
  data () {
    return {
      loading: false,
      saving: false,
      exporting: false,
      syncing: false,
      readOnly: false,
      drawerVisible: false,
      viewportWidth: window.innerWidth,
      query: { ruleName: '', regionCode: undefined },
      rows: [],
      selectedRowKeys: [],
      form: blankForm(),
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: total => `共 ${total} 条`
      },
      columns: [
        { title: '序号', width: 70, customRender: (text, record, index) => (this.pagination.current - 1) * this.pagination.pageSize + index + 1 },
        { title: '运营规则名称', dataIndex: 'ruleName', width: 180 },
        { title: '区域', dataIndex: 'regionName', width: 180 },
        { title: '营业开始时间', dataIndex: 'businessStartTime', width: 140, customRender: text => timeText(text) },
        { title: '营业结束时间', dataIndex: 'businessEndTime', width: 140, customRender: text => timeText(text) },
        { title: '备注', dataIndex: 'remark', scopedSlots: { customRender: 'remark' } },
        { title: '操作项', dataIndex: 'operation', width: 220, scopedSlots: { customRender: 'operation' } }
      ]
    }
  },
  computed: {
    drawerWidth () { return this.viewportWidth < 1200 ? '100%' : 960 },
    drawerTitle () {
      if (this.readOnly) return '查看运营规则模版'
      return this.form.id ? '编辑运营规则模版' : '新增运营规则模版'
    },
    rowSelection () {
      return {
        selectedRowKeys: this.selectedRowKeys,
        onChange: keys => { this.selectedRowKeys = keys.map(key => String(key)) }
      }
    }
  },
  mounted () {
    window.addEventListener('resize', this.onResize)
    loadChinaArea().catch(() => {})
    this.load()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    onResize () { this.viewportWidth = window.innerWidth },
    rowKey (record) { return String(record.id) },
    timeValue,
    timeText,
    buildQuery (pageNum, pageSize) {
      const regionIds = this.query.regionCode ? regionSubtreeIds(this.query.regionCode) : undefined
      return {
        ruleName: this.query.ruleName,
        regionIds: regionIds && regionIds.length ? regionIds : undefined,
        pageNum: pageNum || this.pagination.current,
        pageSize: pageSize || this.pagination.pageSize
      }
    },
    load () {
      this.loading = true
      pageOperationRule(this.buildQuery()).then(res => {
        const page = res.data || {}
        this.rows = page.records || []
        this.pagination.total = Number(page.total || 0)
        this.selectedRowKeys = []
      }).finally(() => { this.loading = false })
    },
    syncSelected () {
      if (!this.selectedRowKeys.length) return this.$message.warning('请选择要同步的运营规则')
      this.syncing = true
      syncOperationRule(this.selectedRowKeys.slice()).then(() => {
        this.$message.success('已同步')
        this.selectedRowKeys = []
      }).finally(() => { this.syncing = false })
    },
    search () { this.pagination.current = 1; this.load() },
    reset () {
      this.query = { ruleName: '', regionCode: undefined }
      this.search()
    },
    onPageChange (pager) {
      this.pagination.current = pager.current
      this.pagination.pageSize = pager.pageSize
      this.load()
    },
    openCreate () {
      this.form = blankForm()
      this.readOnly = false
      this.drawerVisible = true
    },
    fillForm (record) {
      const regionCode = record.regionId == null ? undefined : String(record.regionId)
      this.form = {
        id: record.id,
        ruleName: record.ruleName || '',
        regionCode,
        regionName: regionLabel(regionCode) || record.regionName || '',
        businessStartTime: normalizeTimeText(record.businessStartTime),
        businessEndTime: normalizeTimeText(record.businessEndTime),
        remark: record.remark || ''
      }
    },
    openView (record) {
      this.fillForm(record)
      this.readOnly = true
      this.drawerVisible = true
    },
    openEdit (record) {
      this.fillForm(record)
      this.readOnly = false
      this.drawerVisible = true
    },
    closeDrawer () {
      this.drawerVisible = false
    },
    submit () {
      if (!this.form.ruleName || !this.form.ruleName.trim()) return this.$message.warning('请输入运营规则名称')
      if (!this.form.regionCode) return this.$message.warning('请选择区域')
      if (!this.form.businessStartTime) return this.$message.warning('请选择营业开始时间')
      if (!this.form.businessEndTime) return this.$message.warning('请选择营业结束时间')
      if (this.form.businessEndTime < this.form.businessStartTime) return this.$message.warning('营业结束时间不能早于营业开始时间')
      const payload = {
        id: this.form.id,
        ruleName: this.form.ruleName.trim(),
        regionId: String(this.form.regionCode),
        regionName: regionLabel(this.form.regionCode),
        businessStartTime: this.form.businessStartTime,
        businessEndTime: this.form.businessEndTime,
        remark: (this.form.remark || '').trim()
      }
      this.saving = true
      const request = payload.id ? editOperationRule(payload) : addOperationRule(payload)
      request.then(() => {
        this.$message.success('已保存')
        this.drawerVisible = false
        this.load()
      }).finally(() => { this.saving = false })
    },
    remove (record) {
      this.$confirm({
        title: '确认删除这条运营规则？',
        content: record.ruleName,
        onOk: () => deleteOperationRule(record.id).then(() => {
          this.$message.success('已删除')
          this.load()
        })
      })
    },
    exportRows () {
      this.exporting = true
      pageOperationRule(this.buildQuery(1, 5000)).then(res => {
        const records = (res.data && res.data.records) || []
        const header = ['运营规则名称', '区域', '营业开始时间', '营业结束时间', '备注']
        const lines = [header.join(',')]
        records.forEach(row => {
          lines.push([row.ruleName, row.regionName, this.timeText(row.businessStartTime), this.timeText(row.businessEndTime), row.remark || '无'].map(value => {
            const text = String(value == null ? '' : value).replace(/"/g, '""')
            return `"${text}"`
          }).join(','))
        })
        const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = '运营规则.csv'
        link.click()
        URL.revokeObjectURL(url)
      }).finally(() => { this.exporting = false })
    }
  }
}
</script>

<style scoped>
.operation-rule-page .ant-btn + .ant-btn { margin-left: 8px; }
.operation-rule-page >>> .ant-form-item-control,
.erp-base-grid > label { overflow: visible; }
</style>
